import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import BlockContent from '@sanity/block-content-to-react';

import { useTranslation } from 'react-i18next';

import { HIDDEN_FEATURES } from '../../constants';
import {
  RawPortableText,
  GatsbySanityImageFluid,
  GatsbySanityImageFluidNoBase64,
} from '../../types';
import WhoWeArePageLayout from '../../components/WhoWeArePageLayout';
import serializers from '../../serializers';
import CardCarousel from '../../components/CardCarousel';
import GreenQuote from '../../components/GreenQuote';
import Video from '../../components/Video';
import SEO from '../../components/SEO';

import * as styles from './ascenza.module.scss';

import greenCircles from '../../assets/images/circles/circlelines_green.png';

interface QueryData {
  sanityWhoWeAre: {
    ascenzaSection: {
      image?: {
        asset?: {
          fluid: GatsbySanityImageFluidNoBase64;
        };
      };
      _rawImageText: RawPortableText;
      _rawIntroText: RawPortableText;
      quote: string;
      differentiatingFactorTitle: string;
      _rawDifferentiatingFactorText: RawPortableText;
      cardsCarrousel: Array<{
        _key: string;
        image: {
          asset: {
            fluid: GatsbySanityImageFluid;
          };
        };
        title: string;
        text: string;
      }>;
      statements: Array<{
        _key: string;
        image: {
          asset: {
            fluid: GatsbySanityImageFluid;
          };
        };
        title: string;
        description: string;
      }>;
      _rawPurpose: RawPortableText;
      presentationVideoUrl: string;
    };
  };
}

const ascenza = (): React.ReactElement => {
  const data: QueryData = useStaticQuery(
    graphql`
      {
        sanityWhoWeAre {
          ascenzaSection {
            image {
              asset {
                fluid(maxWidth: 810, maxHeight: 540) {
                  ...GatsbySanityImageFluid_noBase64
                }
              }
            }
            _rawImageText(resolveReferences: { maxDepth: 4 })
            _rawIntroText(resolveReferences: { maxDepth: 4 })
            quote
            differentiatingFactorTitle
            _rawDifferentiatingFactorText(resolveReferences: { maxDepth: 4 })
            cardsCarrousel {
              _key
              image {
                asset {
                  fluid(maxWidth: 700, maxHeight: 480) {
                    ...GatsbySanityImageFluid
                  }
                }
              }
              title
              text
            }
            statements {
              _key
              image {
                asset {
                  fluid(maxWidth: 810, maxHeight: 810) {
                    ...GatsbySanityImageFluid
                  }
                }
              }
              title
              description
            }
            _rawPurpose(resolveReferences: { maxDepth: 4 })
            presentationVideoUrl
          }
        }
      }
    `,
  );
  const ascenza = data.sanityWhoWeAre.ascenzaSection || {};
  const { t } = useTranslation();
  return (
    <WhoWeArePageLayout currentSlug="ascenza">
      <SEO title={t('ascenza_page.title', 'ASCENZA')} urlPath={'/who-we-are/ascenza'} />

      {!!(ascenza.image && ascenza.image.asset) && ascenza._rawImageText && (
        <div className={styles.introSection}>
          <Img
            className={styles.introImage}
            loading="eager"
            fadeIn={false}
            fluid={ascenza.image.asset.fluid}
            alt=""
          />
          <div className={styles.introSmallDescription}>
            <BlockContent blocks={ascenza._rawImageText} serializers={serializers} />
          </div>
        </div>
      )}
      {!!ascenza._rawIntroText && (
        <div className={styles.description}>
          <BlockContent blocks={ascenza._rawIntroText} serializers={serializers} />
        </div>
      )}
      {!!ascenza.quote && <GreenQuote text={ascenza.quote}></GreenQuote>}
      {!!ascenza.differentiatingFactorTitle &&
        !!ascenza._rawDifferentiatingFactorText &&
        !!ascenza.cardsCarrousel && (
          <div className={styles.body}>
            <h2>{ascenza.differentiatingFactorTitle}</h2>
            <BlockContent
              blocks={ascenza._rawDifferentiatingFactorText}
              serializers={serializers}
            />
            <CardCarousel
              cards={ascenza.cardsCarrousel.map(card => ({
                imageFluid: card.image.asset.fluid,
                title: card.title,
                text: card.text,
              }))}
            ></CardCarousel>
          </div>
        )}
      {!!ascenza.statements && (
        <div className={styles.statmentsContainer}>
          {ascenza.statements.map(statement => (
            <div key={statement._key} className={styles.statment}>
              <div className={styles.statmentContent}>
                <h3 className={styles.statmentTitle}>{statement.title}</h3>
                <p className={styles.statmentDescription}>{statement.description}</p>
              </div>
              <div className={styles.statmentImageContainer}>
                <Img
                  className={styles.statmentImage}
                  fluid={statement.image.asset.fluid}
                  alt={statement.title}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!!ascenza._rawPurpose && (
        <div className={styles.wrapper}>
          <img src={greenCircles} className={styles.greenCircles} alt="" />
          <div className={styles.purposeContainer}>
            <BlockContent blocks={ascenza._rawPurpose} serializers={serializers} />
          </div>
        </div>
      )}

      {!!ascenza.presentationVideoUrl && (
        <Video className={styles.video} url={ascenza.presentationVideoUrl}></Video>
      )}
      <div className={styles.buttonWrapper}>
        <a
          className={styles.button}
          href={'https://www.ascenza.com/'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('ascenza_page.more_about_button', 'More About ASCENZA')}
        </a>
      </div>
    </WhoWeArePageLayout>
  );
};

export default ascenza;
